import { useEffect, useState } from 'react';
import { viewportDebugEnabled } from '../lib/viewport';

interface Sample {
  innerHeight: number;
  vvHeight: number;
  offsetTop: number;
  scrollY: number;
  cssHeight: string;
  cssTop: string;
  keyboard: boolean;
  typing: boolean;
}

function sample(): Sample {
  const vv = window.visualViewport;
  const root = document.documentElement;
  return {
    innerHeight: window.innerHeight,
    vvHeight: vv ? Math.round(vv.height * 10) / 10 : -1,
    offsetTop: vv ? Math.round(vv.offsetTop * 10) / 10 : -1,
    scrollY: Math.round(window.scrollY),
    cssHeight: root.style.getPropertyValue('--vv-height') || '(unset)',
    cssTop: root.style.getPropertyValue('--vv-top') || '(unset)',
    keyboard: root.hasAttribute('data-keyboard'),
    typing: root.hasAttribute('data-typing'),
  };
}

/**
 * Live viewport numbers for a device screenshot. Shown when the URL has `?vv=1` (remembered)
 * or localStorage `pa.vvdebug=1`. Updates on the same events the layout uses, plus a slow
 * tick so a stale value never lies.
 */
export function VvDebug() {
  const [enabled] = useState(() => viewportDebugEnabled());
  const [s, setS] = useState<Sample | null>(null);
  useEffect(() => {
    if (!enabled) return;
    const update = () => setS(sample());
    update();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    document.addEventListener('focusin', update);
    document.addEventListener('focusout', update);
    const tick = setInterval(update, 500);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      document.removeEventListener('focusin', update);
      document.removeEventListener('focusout', update);
      clearInterval(tick);
    };
  }, [enabled]);
  if (!enabled || !s) return null;
  return (
    <pre
      aria-hidden
      dir="ltr"
      className="pointer-events-none fixed start-2 z-[100] rounded-md bg-ink/80 px-2 py-1 font-mono text-[11px] leading-4 text-canvas"
      style={{ top: 'calc(var(--vv-top, 0px) + env(safe-area-inset-top) + 56px)' }}
    >
      {`innerHeight ${s.innerHeight}
vv.height   ${s.vvHeight}
vv.offsetTop ${s.offsetTop}
scrollY     ${s.scrollY}
--vv-height ${s.cssHeight}
--vv-top    ${s.cssTop}
keyboard    ${s.keyboard ? 'yes' : 'no'}  typing ${s.typing ? 'yes' : 'no'}`}
    </pre>
  );
}
